// Scannable plan studio. Shared by the brand-page Plans drawer and the
// marketing plan card's open view. No push, pause, or create. A suggested
// build only leaves this view after the operator confirms a USD cap.

import { useEffect, useState, type ReactNode } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Badge, Box, Button, HStack, Input, Modal, ModalBody, ModalContent, ModalFooter,
  ModalHeader, ModalOverlay, SimpleGrid, Spinner, Text, VStack
} from '@chakra-ui/react';
import {
  getPlanDetail, planHrefForLocation, suggestionHref,
  type PlanAdSetView, type PlanBudgetCell, type PlanCampaignNode, type PlanCreativeView,
  type PlanMeasure, type PlanSource, type PlanStatusChip, type PlanStudio, type PlanSuggestedBuild
} from '../../marketingPlans/detail';
import { planPerformanceEnabled, planPerformanceHref } from '../../marketingPlans/performance';
import { getPlanProduction, planProductionEnabled, type PlanProduction } from '../../marketingPlans/production';
import { acceptPlanSuggestion, getPlanSuggestions, type AcceptSuggestionResult, type PlanSuggestionView } from '../../marketingPlans/drafts';
import { planSuggestionsEnabled } from '../../marketingPlans/performance';
import { SuggestionsList } from './SuggestionsList';

export function PlanDetailPanel({
  brandId, planId, revision
}: {
  brandId: string | null;
  planId: string;
  revision?: string | null;
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<PlanStudio | null>(null);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [production, setProduction] = useState<PlanProduction | null>(null);
  const [productionLoading, setProductionLoading] = useState(false);
  const productionOn = planProductionEnabled();
  const suggestionsOn = planSuggestionsEnabled();
  const [suggestions, setSuggestions] = useState<PlanSuggestionView[] | null | undefined>(undefined);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsFor, setSuggestionsFor] = useState<string | null>(null);

  useEffect(() => {
    if (!brandId) {
      setLoading(false);
      setDetail(null);
      setMissing(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setDetail(null);
    setMissing(false);
    setError(null);
    void getPlanDetail(brandId, planId, revision).then((result) => {
      if (cancelled) return;
      setDetail(result.detail);
      setMissing(result.missing);
      setError(result.error);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [brandId, planId, revision]);

  useEffect(() => {
    if (!productionOn || !brandId) {
      setProduction(null);
      setProductionLoading(false);
      return;
    }
    let cancelled = false;
    setProductionLoading(true);
    setProduction(null);
    void getPlanProduction(brandId, planId, revision).then((result) => {
      if (cancelled) return;
      setProduction(result.production);
      setProductionLoading(false);
    });
    return () => { cancelled = true; };
  }, [productionOn, brandId, planId, revision]);

  useEffect(() => {
    if (!suggestionsOn || !brandId) {
      setSuggestions(undefined);
      setSuggestionsLoading(false);
      setSuggestionsFor(null);
      return;
    }
    const requestedBrand = brandId;
    let cancelled = false;
    setSuggestionsLoading(true);
    setSuggestions(undefined);
    setSuggestionsFor(null);
    void getPlanSuggestions(requestedBrand, planId).then((next) => {
      if (cancelled) return;
      setSuggestions(next);
      setSuggestionsFor(requestedBrand);
      setSuggestionsLoading(false);
    });
    return () => { cancelled = true; };
  }, [suggestionsOn, brandId, planId]);

  if (!brandId) return <Muted>Select a brand to open this plan.</Muted>;
  if (loading) {
    return (
      <HStack data-testid="plan-detail-loading">
        <Spinner size="sm" />
        <Text fontSize="sm" color="brand.muted">Loading plan…</Text>
      </HStack>
    );
  }
  if (error) return <Text fontSize="sm" color="red.600">{error}</Text>;
  if (missing || !detail) return <Muted>Plan detail isn't available yet.</Muted>;

  return (
    <PlanDetailView
      detail={detail}
      production={production}
      productionLoading={productionLoading}
      suggestionsEnabled={suggestionsOn}
      suggestions={suggestionsOn ? suggestions : undefined}
      suggestionsLoading={suggestionsOn && suggestionsLoading}
      onAcceptSuggestion={suggestionsFor === brandId && brandId ? async (suggestion) => {
        if (!suggestion.id) return { planId: null, duplicate: false, error: null };
        return acceptPlanSuggestion(brandId, planId, suggestion.id);
      } : undefined}
      performanceHref={planPerformanceEnabled()
        ? planHrefForLocation(
          planPerformanceHref(brandId, planId, detail.revision ?? revision ?? null),
          pathname
        )
        : null}
      onStartGenerate={(build, capUsd) => {
        const href = suggestionHref(build, capUsd, {
          planId,
          revision: detail.revision ?? revision ?? null
        });
        if (href) navigate(href);
      }}
    />
  );
}

export function PlanDetailView({
  detail, onStartGenerate, performanceHref = null, production = null, productionLoading = false,
  suggestionsEnabled = false, suggestions, suggestionsLoading = false, onAcceptSuggestion
}: {
  detail: PlanStudio;
  onStartGenerate: (build: PlanSuggestedBuild, capUsd: number) => void;
  performanceHref?: string | null;
  production?: PlanProduction | null;
  productionLoading?: boolean;
  suggestionsEnabled?: boolean;
  suggestions?: PlanSuggestionView[] | null;
  suggestionsLoading?: boolean;
  onAcceptSuggestion?: (suggestion: PlanSuggestionView) => Promise<AcceptSuggestionResult>;
}) {
  const [pending, setPending] = useState<PlanSuggestedBuild | null>(null);

  return (
    <VStack align="stretch" spacing={6} pb={6} data-testid="plan-studio">
      <Text fontWeight="700" fontSize="lg" color="brand.ink" data-testid="plan-detail-title">{detail.title}</Text>
      {detail.status && <Badge alignSelf="flex-start" w="fit-content">{detail.status}</Badge>}
      {performanceHref && (
        <Button as={RouterLink} to={performanceHref} alignSelf="flex-start" size="sm" variant="outline" data-testid="open-performance">
          Performance
        </Button>
      )}
      <Section title="Structure">
        {detail.campaigns.length === 0 ? <Muted>No campaigns in this plan.</Muted> : (
          <VStack align="stretch" spacing={2}>
            {detail.campaigns.map((campaign, index) => (
              <CampaignBranch key={`${campaign.name}-${index}`} campaign={campaign} defaultOpen={index === 0} />
            ))}
          </VStack>
        )}
      </Section>

      <Section title="Budget">
        {detail.budget.length === 0 ? <Muted>No budget cells recorded.</Muted> : <BudgetTable cells={detail.budget} />}
      </Section>

      <Section title="Creatives">
        <CreativeGrid campaigns={detail.campaigns} creatives={detail.creatives} />
      </Section>

      <Section title="Forecast">
        <SimpleGrid columns={{ base: 2, md: 3 }} spacing={2}>
          <KpiTile testId="kpi-spend" label="Spend" measure={detail.forecast.spend} />
          <KpiTile testId="kpi-purchases" label="Purchases" measure={detail.forecast.purchases} />
          <KpiTile testId="kpi-cpa" label="CPA" measure={detail.forecast.cpa} />
          <KpiTile testId="kpi-roas" label="ROAS" measure={detail.forecast.roas} />
          <KpiTile testId="kpi-revenue" label="Revenue" measure={detail.forecast.revenue} />
        </SimpleGrid>
        <Box mt={3} data-testid="kpi-incremental">
          <Text fontSize="xs" color="brand.muted" textTransform="uppercase" letterSpacing="0.04em">Incremental impact on sales</Text>
          <MeasureText measure={detail.forecast.incremental} />
          {detail.forecast.incrementalNote && (
            <Text fontSize="sm" mt={1} data-testid="incremental-note">{detail.forecast.incrementalNote}</Text>
          )}
        </Box>
        <Box mt={3}>
          <Text fontSize="xs" color="brand.muted" textTransform="uppercase" letterSpacing="0.04em" mb={1}>Method and assumptions</Text>
          <MutedOr text={detail.forecast.method} />
        </Box>
      </Section>

      <Section title="Coverage gaps">
        {detail.coverageUnavailable ? (
          <Text fontSize="sm" data-testid="coverage-unavailable">{detail.coverageUnavailable}</Text>
        ) : detail.gaps.length === 0 ? <Muted>No coverage gaps recorded.</Muted> : (
          <VStack align="stretch" spacing={1} data-testid="plan-gaps">
            {detail.gaps.map((gap) => <Text key={gap} fontSize="sm">{gap}</Text>)}
          </VStack>
        )}
        {production && production.coverage.length > 0 && (
          <VStack align="stretch" spacing={1} mt={3} data-testid="production-coverage">
            <Text fontSize="xs" color="brand.muted" textTransform="uppercase" letterSpacing="0.04em">Filled by production</Text>
            {production.coverage.map((line) => <Text key={line.text} fontSize="sm">{line.text}</Text>)}
          </VStack>
        )}
        {productionLoading && !production && (
          <Text fontSize="sm" color="brand.muted" mt={2} data-testid="production-loading" aria-live="polite">Loading production status…</Text>
        )}
      </Section>

      {production && (
        <Section title="Production">
          {production.workflowVersion && (
            <Text fontSize="sm" data-testid="production-workflow">Workflow version {production.workflowVersion}</Text>
          )}
          {production.revision && (
            <Text fontSize="sm" color="brand.muted">Revision {production.revision}</Text>
          )}
          {production.unavailableReason && (
            <Text fontSize="sm" mt={1} data-testid="production-unavailable">{production.unavailableReason}</Text>
          )}
          {production.jobs.length > 0 && (
            <VStack align="stretch" spacing={2} mt={2} data-testid="production-jobs">
              {production.jobs.map((job, index) => (
                <Box key={`${job.label}-${index}`}>
                  <Text fontSize="sm" fontWeight="700">
                    {job.label}{job.stage ? ` — ${job.stage}` : ''}
                  </Text>
                  <JobMeta job={job} headerVersion={production.workflowVersion} />
                </Box>
              ))}
            </VStack>
          )}
          {production.jobs.length === 0 && !production.unavailableReason && <Muted>No production jobs yet.</Muted>}
        </Section>
      )}

      {suggestionsEnabled && (
        <Section title="Suggestions">
          {suggestionsLoading ? (
            <Text fontSize="sm" color="brand.muted">Loading suggestions…</Text>
          ) : suggestions === undefined ? null : (
            <SuggestionsList suggestions={suggestions} onAccept={onAcceptSuggestion} />
          )}
        </Section>
      )}

      {detail.coverageUnavailable ? null : (
        <Section title="Suggested builds">
          {detail.builds.length === 0 ? <Muted>No suggested builds.</Muted> : (
            <VStack align="stretch" spacing={2}>
              {detail.builds.map((build, index) => (
                <HStack key={`${build.action}-${index}`} justify="space-between" align="center" spacing={3} borderWidth="1px" borderColor="brand.border" borderRadius="md" px={3} py={2}>
                  <Box minW={0}>
                    <Text fontSize="sm" fontWeight="700">{build.action}</Text>
                    <Text fontSize="xs" color="brand.muted">
                      Estimated cost {build.estimatedCost ?? 'Not available'}
                    </Text>
                  </Box>
                  {build.route ? (
                    <Button size="sm" variant="outline" flexShrink={0} onClick={() => setPending(build)}>
                      Generate
                    </Button>
                  ) : (
                    <Text fontSize="xs" color="brand.muted" flexShrink={0}>This suggestion can't be started here.</Text>
                  )}
                </HStack>
              ))}
              {detail.builds.some((build) => build.needsLineCampaign) && (
                <Text fontSize="xs" color="brand.muted" data-testid="plan-line-campaign">
                  This campaign has to be the one on this plan line. A different campaign is refused.
                </Text>
              )}
            </VStack>
          )}
        </Section>
      )}

      <CapDialog
        build={pending}
        onClose={() => setPending(null)}
        onConfirm={(capUsd) => {
          if (!pending) return;
          const next = pending;
          setPending(null);
          onStartGenerate(next, capUsd);
        }}
      />
    </VStack>
  );
}

function CampaignBranch({ campaign, defaultOpen }: { campaign: PlanCampaignNode; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box borderWidth="1px" borderColor="brand.border" borderRadius="md">
      <NodeButton
        open={open}
        onClick={() => setOpen((value) => !value)}
        name={campaign.name}
        action={campaign.action}
        status={campaign.status}
        statusLabel={campaign.statusLabel}
        source={campaign.source}
        ariaLabel={`Toggle campaign ${campaign.name}`}
      />
      {open && (
        <VStack align="stretch" spacing={2} px={3} pb={3}>
          {campaign.adSets.length === 0 ? <Muted>No ad sets.</Muted> : campaign.adSets.map((adSet, index) => (
            <AdSetBranch key={`${adSet.name}-${index}`} adSet={adSet} />
          ))}
        </VStack>
      )}
    </Box>
  );
}

function AdSetBranch({ adSet }: { adSet: PlanAdSetView }) {
  const [open, setOpen] = useState(true);
  return (
    <Box borderWidth="1px" borderColor="brand.border" borderRadius="md" bg="white">
      <NodeButton
        open={open}
        onClick={() => setOpen((value) => !value)}
        name={adSet.name}
        action={null}
        status={adSet.status}
        statusLabel={adSet.statusLabel}
        source={adSet.source}
        ariaLabel={`Toggle ad set ${adSet.name}`}
      />
      {open && (
        <Box px={3} pb={3}>
          <Text fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mb={1}>Targeting</Text>
          <TargetingList targeting={adSet.targeting} />
          <Text fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mt={3} mb={1}>Ads</Text>
          {adSet.ads.length === 0 ? <Muted>No ads.</Muted> : (
            <VStack align="stretch" spacing={2}>
              {adSet.ads.map((ad, index) => (
                <Box key={`${ad.name}-${index}`}>
                  <HStack spacing={2} flexWrap="wrap">
                    <Text fontSize="sm" color="brand.ink">{ad.name}</Text>
                    <StatusBadge status={ad.status} label={ad.statusLabel} />
                    <SourceBadge source={ad.source} />
                  </HStack>
                  {ad.creatives.map((creative, creativeIndex) => (
                    <Text key={`${creative.id ?? creative.name ?? 'creative'}-${creativeIndex}`} fontSize="xs" color="brand.muted">
                      {creativeSummary(creative)}
                    </Text>
                  ))}
                </Box>
              ))}
            </VStack>
          )}
        </Box>
      )}
    </Box>
  );
}

function creativeSummary(creative: PlanCreativeView): string {
  return [
    creative.type,
    creative.statusLabel,
    creative.copyApproval ? `Copy ${creative.copyApproval}` : null,
    creative.reachSocialCreated == null ? null : creative.reachSocialCreated ? 'Reach Social created' : 'Not Reach Social created',
    creative.colorway
  ].filter((part): part is string => Boolean(part)).join(' · ');
}

function NodeButton({
  open, onClick, name, action, status, statusLabel, source, ariaLabel
}: {
  open: boolean;
  onClick: () => void;
  name: string;
  action: string | null;
  status: PlanStatusChip;
  statusLabel: string | null;
  source: PlanSource | null;
  ariaLabel: string;
}) {
  return (
    <Box
      as="button"
      type="button"
      w="100%"
      textAlign="left"
      py={2}
      px={3}
      onClick={onClick}
      aria-expanded={open}
      aria-label={ariaLabel}
    >
      <HStack spacing={2} flexWrap="wrap">
        <Text fontSize="xs" color="brand.muted" w="12px">{open ? '▾' : '▸'}</Text>
        <Text fontSize="sm" fontWeight="700" color="brand.ink">{name}</Text>
        {action && <Badge colorScheme="purple" variant="subtle">{action}</Badge>}
        <StatusBadge status={status} label={statusLabel} />
        <SourceBadge source={source} />
      </HStack>
    </Box>
  );
}

function TargetingList({ targeting }: { targeting: PlanAdSetView['targeting'] }) {
  const rows: Array<[string, string | null]> = [
    ['Geo', targeting.geo],
    ['Ages', targeting.ages],
    ['Gender', targeting.gender],
    ['Exclusions', targeting.exclusions],
    ['Advantage audience', targeting.advantageAudience],
    ['Placements', targeting.placements]
  ];
  return (
    <VStack align="stretch" spacing={1}>
      {rows.map(([label, value]) => (
        <HStack key={label} align="flex-start" spacing={3}>
          <Text fontSize="xs" color="brand.muted" w="140px" flexShrink={0}>{label}</Text>
          <Text fontSize="sm">{value ?? 'Not set'}</Text>
        </HStack>
      ))}
    </VStack>
  );
}

function BudgetTable({ cells }: { cells: PlanBudgetCell[] }) {
  return (
    <Box overflowX="auto">
      <Box as="table" w="100%" fontSize="sm" sx={{ borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {['Cell', 'Weekly', 'Flight', 'New spend', 'Existing spend'].map((label) => (
              <Text as="th" key={label} textAlign="left" py={1} pr={3} fontSize="xs" color="brand.muted">{label}</Text>
            ))}
          </tr>
        </thead>
        <tbody>
          {cells.map((cell) => (
            <tr key={cell.name}>
              <Text as="td" py={1} pr={3} fontWeight="700">{cell.name}</Text>
              <MoneyCell value={cell.weekly} />
              <MoneyCell value={cell.flight} />
              <MoneyCell value={cell.newSpend} />
              <MoneyCell value={cell.existingSpend} />
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

function MoneyCell({ value }: { value: string | null }) {
  return <Text as="td" py={1} pr={3}>{value ?? 'Not available'}</Text>;
}

function CreativeGrid({ campaigns, creatives }: { campaigns: PlanCampaignNode[]; creatives: PlanCreativeView[] }) {
  const linkedIds = new Set(campaigns.flatMap((campaign) => campaign.adSets.flatMap((adSet) => adSet.ads.flatMap((ad) => ad.creatives.map((creative) => creative.id).filter((id): id is string => Boolean(id))))));
  const unlinked = creatives.filter((creative) => !creative.id || !linkedIds.has(creative.id));
  const groups = campaigns.flatMap((campaign) => campaign.adSets.flatMap((adSet) => adSet.ads.map((ad) => ({
    key: `${campaign.name}-${adSet.name}-${ad.name}`,
    heading: ad.name,
    context: `${campaign.name} · ${adSet.name}`,
    items: ad.creatives
  }))));
  if (groups.length === 0 && unlinked.length === 0) return <Muted>No creatives recorded.</Muted>;
  return (
    <VStack align="stretch" spacing={4}>
      {unlinked.length > 0 && (
        <Box data-testid="plan-creatives">
          <Text fontSize="sm" fontWeight="700">Plan creatives</Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2} mt={2}>
            {unlinked.map((item, index) => (
              <CreativeCard key={`${item.id ?? item.name ?? 'creative'}-${index}`} creative={item} />
            ))}
          </SimpleGrid>
        </Box>
      )}
      {groups.map((group) => (
        <Box key={group.key}>
          <Text fontSize="sm" fontWeight="700">{group.heading}</Text>
          <Text fontSize="xs" color="brand.muted" mb={2}>{group.context}</Text>
          {group.items.length === 0 ? <Muted>No creatives for this ad.</Muted> : (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
              {group.items.map((item, index) => (
                <CreativeCard key={`${item.name ?? 'creative'}-${index}`} creative={item} />
              ))}
            </SimpleGrid>
          )}
        </Box>
      ))}
    </VStack>
  );
}

function CreativeCard({ creative }: { creative: PlanCreativeView }) {
  const title = creative.name ?? creative.id;
  const exists = creative.exists == null ? null : creative.exists ? 'Exists' : 'Missing';
  return (
    <Box borderWidth="1px" borderColor="brand.border" borderRadius="md" px={3} py={2} data-testid={creative.id ? `creative-${creative.id}` : undefined}>
      {title && <Text fontSize="sm" fontWeight="700" mb={1}>{title}</Text>}
      <HStack spacing={2} flexWrap="wrap" mb={1}>
        <SourceBadge source={creative.source} />
        {creative.reachSocialCreated != null && (
          <Badge colorScheme={creative.reachSocialCreated ? 'green' : 'gray'} variant="subtle" data-testid="reach-social-created">
            {creative.reachSocialCreated ? 'Reach Social created' : 'Not Reach Social created'}
          </Badge>
        )}
        {(creative.statusLabel ?? exists) && (
          <Badge colorScheme={creative.exists ? 'green' : creative.exists == null ? 'gray' : 'orange'} variant="subtle">
            {creative.statusLabel ?? exists}
          </Badge>
        )}
      </HStack>
      {creative.type && <Text fontSize="xs" color="brand.muted">Type {creative.type}</Text>}
      {creative.colorway && <Text fontSize="xs" color="brand.muted">Colorway {creative.colorway}</Text>}
      {creative.copyApproval && <Text fontSize="xs" color="brand.muted">Copy {creative.copyApproval}</Text>}
    </Box>
  );
}

function KpiTile({ testId, label, measure }: { testId: string; label: string; measure: PlanMeasure }) {
  return (
    <Box data-testid={testId} borderWidth="1px" borderColor="brand.border" borderRadius="md" px={3} py={2}>
      <Text fontSize="10px" color="brand.muted" textTransform="uppercase" letterSpacing="0.04em">{label}</Text>
      <MeasureText measure={measure} />
    </Box>
  );
}

function MeasureText({ measure }: { measure: PlanMeasure }) {
  if (measure.value == null) {
    return (
      <Box>
        <Text fontSize="md" fontWeight="700" color="brand.muted">Not available</Text>
        {measure.reason && <Text fontSize="xs" color="brand.muted">{measure.reason}</Text>}
      </Box>
    );
  }
  return <Text fontSize="md" fontWeight="700" color="brand.ink">{measure.value}</Text>;
}

function CapDialog({
  build, onClose, onConfirm
}: {
  build: PlanSuggestedBuild | null;
  onClose: () => void;
  onConfirm: (capUsd: number) => void;
}) {
  const [capInput, setCapInput] = useState('');
  useEffect(() => {
    if (!build) return;
    setCapInput(build.estimatedCostUsd == null ? '' : roundUsdUp(build.estimatedCostUsd).toFixed(2));
  }, [build]);
  const cap = parseCap(capInput);
  return (
    <Modal isOpen={build != null} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent mx={4}>
        <ModalHeader fontSize="md">Confirm a USD cap</ModalHeader>
        <ModalBody>
          <Text fontSize="sm" mb={2}>{build?.action}</Text>
          <Text fontSize="sm" color="brand.muted" mb={3}>
            Estimated cost {build?.estimatedCost ?? 'Not available'}
          </Text>
          <Text as="label" htmlFor="plan-build-cap" fontSize="xs" color="brand.muted">USD cap</Text>
          <Input
            id="plan-build-cap"
            aria-label="USD cap"
            mt={1}
            size="sm"
            inputMode="decimal"
            value={capInput}
            onChange={(event) => setCapInput(event.target.value)}
          />
          <Text fontSize="xs" color="brand.muted" mt={2}>
            Default is the estimate rounded up. Nothing runs until you confirm this cap.
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            variant="brand"
            ml={3}
            isDisabled={cap == null}
            onClick={() => { if (cap != null) onConfirm(cap); }}
          >
            Start generate
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function StatusBadge({ status, label }: { status: PlanStatusChip; label: string | null }) {
  if (!label) return null;
  const color = status === 'live' ? 'green' : status === 'created_paused' ? 'yellow' : status === 'planned' ? 'purple' : 'gray';
  return <Badge colorScheme={color} variant="subtle">{label}</Badge>;
}

function SourceBadge({ source }: { source: PlanSource | null }) {
  if (!source) return null;
  return (
    <Badge colorScheme={source === 'reach_social' ? 'purple' : 'gray'} variant="outline">
      {source === 'reach_social' ? 'Reach Social' : 'Existing'}
    </Badge>
  );
}

function JobMeta({ job, headerVersion }: { job: PlanProduction['jobs'][number]; headerVersion: string | null }) {
  const bits = [
    job.revision ? `Revision ${job.revision}` : null,
    job.attempt,
    job.estimate ? `Estimate ${job.estimate}` : null,
    job.cap ? `Cap ${job.cap}` : null,
    job.actual ? `Actual ${job.actual}` : null,
    job.creativeCount != null && job.creativeCount > 0
      ? `${job.creativeCount} creative${job.creativeCount === 1 ? '' : 's'} recorded`
      : null,
    job.workflowVersion && job.workflowVersion !== headerVersion ? `Workflow version ${job.workflowVersion}` : null
  ].filter((bit): bit is string => Boolean(bit));
  if (!bits.length) return null;
  return <Text fontSize="xs" color="brand.muted">{bits.join(' · ')}</Text>;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box>
      <Text fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mb={2}>
        {title}
      </Text>
      {children}
    </Box>
  );
}

function Muted({ children }: { children: string }) {
  return <Text fontSize="sm" color="brand.muted">{children}</Text>;
}

function MutedOr({ text }: { text: string | null }) {
  return <Text fontSize="sm" color={text ? 'brand.ink' : 'brand.muted'}>{text ?? 'Not available'}</Text>;
}

function roundUsdUp(value: number): number {
  return Math.ceil(value * 100 - 1e-8) / 100;
}

function parseCap(raw: string): number | null {
  const trimmed = raw.trim().replace(/^\$/, '').replace(/,/g, '');
  if (!/^\d*\.?\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value * 100) / 100;
}
